import React, { useState } from "react";
import moment from "moment";
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js';

import editIcon from "../../../../../../../Assets/images/edit-icon.svg";
import deleteIcon from "../../../../../../../Assets/images/delete-icon.svg";
import Loader from "../../../../../../../Context/Loader/Loader";
import ConfirmationModal from "../../../../../../../Components/ConfirmationBox/ConfirmationModal";

import apiClient from "../../../../../../../Config/apiClient";
import { showSuccessToast, showErrorToast } from "../../../../../../../Context/Toaster/Toaster";
import AddJobsModal from "../../AddJobs/AddJobsModal";
import { DATE_TIME_FORMAT } from "../../../../Utils/Constants";

const jobsPath = process.env.REACT_APP_CC_JOBS_BASE_PATH;

const JobDetailsTab = ({ jobData, refresh, onStatusChange, triggerAddModal }) => {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false);

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;

    if (selectedStatus === "Completed") {
      // Ask for confirmation before proceeding
      setShowCompletionConfirm(true);
      return;
    }

    await updateJobStatus(selectedStatus);
  };

  const updateJobStatus = async (statusToUpdate) => {
    try {
      setIsUpdating(true);
      const response = await apiClient.put(
        `${jobsPath}/update-status`,
        { status: statusToUpdate },
        { params: { id: jobData.id } }
      );

      if (response.data.status === "SUCCESS") {
        showSuccessToast("Job status updated successfully");
        if (onStatusChange) onStatusChange(statusToUpdate);
        jobData.job_status = statusToUpdate;
        setSelectedStatus("");
      } else {
        showErrorToast(response.data?.message || "Failed to update job status");
      }
    } catch (err) {
      console.error(err);
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong while updating job status";
        showErrorToast(backendMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteJob = async () => {
    try {
      const response = await apiClient.delete(`${jobsPath}/${jobData.id}`);

      if (response.data.status === "SUCCESS") {
        showSuccessToast("Job deleted successfully");

        const modal = document.getElementById("viewJob");
        if (modal) {
          modal.classList.remove("show");
          modal.setAttribute("aria-hidden", "true");
          modal.style.display = "none";
          document.body.classList.remove("modal-open");
          document.body.style = "";
          const backdrop = document.querySelector(".modal-backdrop");
          if (backdrop) backdrop.remove();
        }

        setShowDeleteModal(false);
        if (typeof refresh === "function") {
          refresh();
        }

      } else {
        const errorCode = response.data.error_data?.code;
        const errorMessage = response.data.error_data?.message || "Failed to delete job";

        if (errorCode === "JOB-DELETE-NOT-ALLOWED") {
          showErrorToast("You can only delete jobs with 'Not Started' status.");
        } else if (errorCode === "JOB-HAS-TRUCKS") {
          showErrorToast("This job cannot be deleted because trucks have already applied.");
        } else {
          showErrorToast(errorMessage);
        }
      }
    } catch (err) {
      console.error(err);
      if (err.response?.data?.error_data?.message) {
        showErrorToast(err.response.data.error_data.message);
      } else {
        showErrorToast("An unexpected error occurred while deleting the job.");
      }
    }
  };

  return (
    <div className="vr-inner">
      <div className="vr-top">
        <div className="vrt-left">
          <h3>Job ID</h3>
          <p>{jobData.job_name}</p>
        </div>
        <div className="vrt-right">
          <a href="#" className={`sep-line ${["Completed", "Cancelled"].includes(jobData.job_status) ? "job-disabled" : ""}`}
            onClick={(e) => {
              e.preventDefault();

              const modalEl = document.getElementById("viewJob");
              const modalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);

              if (modalInstance) {
                modalEl.addEventListener(
                  "hidden.bs.modal",
                  () => {
                    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

                    triggerAddModal();
                  },
                  { once: true }
                );

                modalInstance.hide();
              } else {
                document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
                triggerAddModal();
              }
            }}

            style={{ pointerEvents: ["Completed", "Cancelled"].includes(jobData.job_status) ? "none" : "auto", opacity: ["Completed", "Cancelled"].includes(jobData.job_status) ? 0.5 : 1, }}
          >
            <img src={editIcon} alt="Edit" />
            Edit
          </a>

          <a href="#" className={`${jobData.job_status !== "Not Started" ? "job-disabled" : ""}`} style={{ pointerEvents: jobData.job_status !== "Not Started" ? "none" : "auto", opacity: jobData.job_status !== "Not Started" ? 0.5 : 1, }} onClick={(e) => { e.preventDefault(); setShowDeleteModal(true); }}>
            <img src={deleteIcon} alt="Cancel" />
            Delete
          </a>
        </div>
      </div>

      <div className="vrt-content">
        <div className="row">
          <div className="col-md-12">
            <div className="vrt-split vrt-padding">
              <h3>Description</h3>
              <p>{jobData.job_description}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Job Type</h3>
              <p>{jobData.job_type}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Truck Type</h3>
              <p>{jobData.truck_type}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Start Location</h3>
              <p>
                {jobData.start_location
                  ? jobData.start_location
                  : (jobData.start_location_latitude != null && jobData.start_location_longitude != null)
                    ? `(${jobData.start_location_latitude}, ${jobData.start_location_longitude})`
                    : "N/A"}
              </p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Dump Location</h3>
              <p>
                {jobData.end_location
                  ? jobData.end_location
                  : (jobData.end_location_latitude != null && jobData.end_location_longitude != null)
                    ? `(${jobData.end_location_latitude}, ${jobData.end_location_longitude})`
                    : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Start Time</h3>
              <p>{moment.utc(jobData.start_date_time).local().format(DATE_TIME_FORMAT)}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>End Time</h3>
              <p>{moment.utc(jobData.end_date_time).local().format(DATE_TIME_FORMAT)}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Pay Rate (CAD)</h3>
              <p>${jobData.payment_rate}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Payment Type</h3>
              <p>{jobData.payment_type}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Trucks Needed</h3>
              <p>{jobData.num_trucks_required}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Trucks Available</h3>
              <p>{jobData.num_trucks_applied}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Supervisor</h3>
              <p>{jobData.supervisor ? `${jobData.supervisor.first_name} ${jobData.supervisor.last_name}` : "N/A"}</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <h3>Job Status</h3>
              <p>{jobData.job_status}</p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <select className="common-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} disabled={["Completed", "Cancelled"].includes(jobData.job_status)}>
                <option value="">--Select Status--</option>
                {jobData.job_status === "Not Started" && (
                  <>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </>
                )}
                {jobData.job_status === "In Progress" && (
                  <>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="vrt-split vrt-padding">
              <button type="button" className={`firstNext next cp-button save-btn float-start d-flex align-items-center justify-content-center ${["Completed", "Cancelled"].includes(jobData.job_status) ? "disabled-btn" : ""}`} onClick={handleStatusUpdate} disabled={["Completed", "Cancelled"].includes(jobData.job_status)} >
                {isUpdating ? <Loader /> : "Update"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddJobsModal
        jobData={jobData}
        showModal={showAddModal}
        setRefresh={onStatusChange}
        setShowModal={setShowAddModal}
      />

      <ConfirmationModal
        id="deleteJobModal"
        show={showDeleteModal}
        title="Delete Job"
        message={`Are you sure you want to delete this ${jobData?.job_name || "job"}?`}
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={handleDeleteJob}
        onCancel={() => setShowDeleteModal(false)}
      />

      <ConfirmationModal
        id="confirmJobCompletion"
        show={showCompletionConfirm}
        title="Confirm Job Completion"
        message="Yes, I have entered the Truck hours/tons."
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={async () => {
          setShowCompletionConfirm(false);
          await updateJobStatus("Completed");
        }}
        onCancel={() => setShowCompletionConfirm(false)}
      />

    </div>
  );
};

export default JobDetailsTab;